/**
 * Solicitud de eliminación de un adjunto del cajón (reunión con las
 * supervisoras, 2026-09-18; spec 2026-09-24-solicitud-eliminacion-design.md).
 *
 * Una auxiliar que necesita borrar una contabilización, un XML o un vinculado
 * no borra: pide permiso con un motivo. Las supervisoras reciben un correo y,
 * al aprobar en DAYSI, se ejecuta el borrado de siempre (que queda en la
 * papelera). Este módulo decide lo que se puede decidir sin Firestore; los
 * thunks (actions/database/deletion_requests.js) hacen las escrituras.
 *
 * Módulo puro: sin React/Redux/Firestore, para poder testearlo directo.
 */

import { toAccountingList } from './accounting_refs';

export const MIN_REASON_LENGTH = 10;

/** Lo que se puede pedir eliminar: el cajón del documento y el documento completo. */
export const DELETABLE_KINDS = ['accounting', 'xml', 'link', 'document'];

export const KIND_LABELS = { accounting: 'Contabilización', xml: 'XML', link: 'Vinculado', document: 'Documento' };

const WITH_ARTICLE = {
  accounting: 'la contabilización',
  xml: 'el XML',
  link: 'el archivo vinculado',
  document: 'el documento',
};

/** "la contabilización", "el XML"… para armar frases sin errores de género. */
export const kindWithArticle = (kind) => WITH_ARTICLE[kind] || 'el archivo';

/** Mensaje de error si el motivo no alcanza, o null si está bien. */
export const validateReason = (reason) => {
  const r = String(reason || '').trim();
  return r.length >= MIN_REASON_LENGTH
    ? null
    : `Escribe el motivo de la eliminación (mínimo ${MIN_REASON_LENGTH} caracteres).`;
};

/**
 * Datos de la solicitud, sin `requestedAt` (el thunk le pone el
 * serverTimestamp). No incluye campos de resolución ni de aviso: las reglas
 * rechazan una solicitud que nazca con ellos.
 */
export const buildDeletionRequest = ({
  kind, itemName, itemPath, itemUrl, parentPath, parentName,
  customerId, customerName, reason, uid, userName,
}) => {
  if (!DELETABLE_KINDS.includes(kind)) throw new Error(`Tipo de adjunto desconocido: ${kind}`);
  const invalid = validateReason(reason);
  if (invalid) throw new Error(invalid);
  if (!itemPath || !parentPath || !uid) throw new Error('Faltan datos de la solicitud.');
  return {
    kind,
    itemName: itemName || '',
    itemPath,
    // Solo URLs de Firebase Storage: termina en un href que abre una
    // supervisora (ver isSafeStorageUrl).
    itemUrl: isSafeStorageUrl(itemUrl) ? itemUrl : null,
    parentPath,
    parentName: parentName || null,
    customerId: customerId || null,
    customerName: customerName || null,
    reason: String(reason).trim(),
    status: 'pending',
    requestedBy: uid,
    requestedByName: userName || null,
  };
};

/**
 * Se evalúa DENTRO de la transacción de aprobar o rechazar: solo una
 * supervisora puede pasar una solicitud de `pending` a resuelta.
 */
export const approvalDecision = (current) => {
  if (!current) return { ok: false, message: 'La solicitud ya no existe.' };
  if (current.status === 'pending') return { ok: true };
  if (current.status === 'approved') return { ok: false, message: 'Otra supervisora ya aprobó esta solicitud.' };
  return { ok: false, message: 'Esta solicitud ya fue atendida.' };
};

/**
 * ¿El adjunto pedido sigue colgado del documento? Se comprueba antes de
 * borrar porque `deleteXML` borra el XML ACTUAL del documento: si alguien
 * subió uno nuevo después de la solicitud, aprobar lo borraría a él.
 */
export const isStillAttached = ({ kind, parentData, itemPath }) => {
  if (!parentData || !itemPath) return false;
  if (kind === 'accounting') return toAccountingList(parentData.accounting).some((r) => r?.path === itemPath);
  if (kind === 'xml') return parentData.xml?.path === itemPath;
  if (kind === 'link') return (Array.isArray(parentData.links) ? parentData.links : []).some((r) => r?.path === itemPath);
  return false;
};

// Mensajes con que los borrados del cajón (documents.js) dicen "no hay nada
// que borrar". Si cambian allá, hay que actualizarlos aquí.
const GONE_PATTERNS = [/no existe/i, /Estructura de archivo incorrecta/i, /no pertenece a este archivo/i];

/** 'gone' = ya no había nada que borrar; 'failed' = falló de verdad. */
export const classifyExecutionError = (err) => {
  const msg = err?.message || '';
  return GONE_PATTERNS.some((re) => re.test(msg)) ? 'gone' : 'failed';
};

/**
 * ¿Hay que ejecutar (o reintentar) el borrado de esta solicitud? Una aprobada
 * sin executedAt no alcanzó a borrar: falló después de la transacción, o se
 * cerró la pestaña. Sin esto quedaba "Aprobada" sin haber borrado nada, sin
 * aviso a quien pidió y sin forma de reintentar.
 */
export const needsExecution = (req) => req?.status === 'approved' && !req?.executedAt;

// URL de descarga de Firebase Storage. El `/` final del host impide trucos
// como firebasestorage.googleapis.com.evil.com.
const STORAGE_URL_PREFIX = 'https://firebasestorage.googleapis.com/';

/**
 * ¿Es segura para ponerla en un href? Solo URLs de Firebase Storage. La
 * solicitud la crea la auxiliar y la abre una supervisora: un `javascript:`
 * ejecutaría código en la sesión de la supervisora.
 */
export const isSafeStorageUrl = (url) =>
  typeof url === 'string' && url.startsWith(STORAGE_URL_PREFIX);

// Códigos de Firebase que se pueden explicar sin jerga.
const FRIENDLY_BY_CODE = {
  'permission-denied': 'No tienes permiso para hacer esta acción.',
  unavailable: 'No hay conexión con el servidor. Revisa tu internet e intenta de nuevo.',
  'deadline-exceeded': 'No hay conexión con el servidor. Revisa tu internet e intenta de nuevo.',
  aborted: 'La solicitud cambió mientras la atendías. Recarga la página e intenta de nuevo.',
  'failed-precondition': 'La solicitud cambió mientras la atendías. Recarga la página e intenta de nuevo.',
};

/**
 * Mensaje para la usuaria. Los errores de Firebase (traen `code` de texto)
 * se traducen o caen al genérico, nunca se muestran en inglés. Los propios
 * (sin código, o code 0 de los thunks de documents.js) ya vienen en español.
 */
export const friendlyError = (err, fallback) => {
  const code = typeof err?.code === 'string' ? err.code.replace(/^[a-z]+\//, '') : null;
  if (code) return FRIENDLY_BY_CODE[code] || fallback;
  return err?.message || fallback;
};

/**
 * Id del índice de solicitudes pendientes (`deletion_request_pending/{id}`),
 * uno por adjunto o documento: así dos personas no piden lo mismo y todas ven
 * que ya está pedido. Un id de Firestore no admite '/', y los que tienen forma
 * __x__ están reservados; por eso '/' → '|' y un prefijo fijo.
 */
export const pendingKeyFor = (itemPath) =>
  itemPath ? `p-${String(itemPath).replace(/\//g, '|')}` : null;

/**
 * Motivo para NO borrar un documento, o null. Igual que el borrado directo:
 * un documento con contabilización o con la marca de contabilizado no se
 * elimina. Si se lo agregaron después de la solicitud, no se borra.
 */
export const documentBlock = (itemData) => {
  if (toAccountingList(itemData?.accounting).length > 0) {
    return 'El documento ahora tiene contabilización; primero hay que quitarla.';
  }
  if (itemData?.accounted) {
    return 'El documento ahora tiene la marca de contabilizado; primero hay que quitarla.';
  }
  return null;
};
